import React, { useState, useEffect } from 'react';
import * as yup from 'yup';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';
import { setTaxbill } from '../../modules/taxIssuedModule/taxbill';
import TaxBillColAndPayForm from '../../components/taxIssuedmng/TaxBillColAndPayForm';
import * as taxbillApi from '../../lib/api/taxbill';
import * as billCollectApi from '../../lib/api/billCollect';
import * as billCancelApi from '../../lib/api/billCancel';
import BillCollectAddDialog from '../../components/project_orders/BillCollectAddDialog';
import ReasonDialog from '../../components/common/dialog/ReasonDialog';
import { useSelector } from 'react-redux';


// 레거시 수금(지급) 입력을 BillCollectAddDialog/BillCollectService로 통합(2026-10-21) -
// 설계: obsidian bito/수금·지급 계획 N개 설계(2026-10-21).md §8 "2차 릴리스 TODO" 4번.
// 이 컨테이너는 더 이상 PROC_COMP_YN/PROC_COMP_DT를 직접 입력받지 않는다 - 실제 수금 등록은
// BillCollectService.addCollect를 거치고, 서버(syncInvoiceStatus)가 두 플래그를 derive한다.
const TaxBillColAndPayFormContainer = () => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const [activeStep, setActiveStep] = useState(0);
  const [collectDialogOpen, setCollectDialogOpen] = useState(false);
  const [collectSummary, setCollectSummary] = useState(null);
  const [collectSubmitting, setCollectSubmitting] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const { taxbill, user } = useSelector(({ taxIssuedModule, auth }) => ({
    taxbill: taxIssuedModule.taxbill.taxbill,
    user: auth.user,
  }));

  useEffect(() => {
    if (taxbill) {
      let step = getActiveStep(taxbill);
      setActiveStep(step);
    }
  }, [taxbill, activeStep, dispatch]);

  const getActiveStep = (taxbill) => {
    let step = 0;
    if (taxbill.reqPlanYn === 'Y' && taxbill.reqCompYn === 'N') {
      //발행완료여부 체크
      step = 1;
    } else if (taxbill.reqCompYn === 'Y' && taxbill.procPlanYn === 'N') {
      //지급(수금) 요청 체크
      step = 2;
    } else if (taxbill.procPlanYn === 'Y' && taxbill.procCompYn === 'N') {
      //지급(수금) 요청 체크

      step = 3;
    } else if (taxbill.procCompYn === 'Y' && taxbill.rsltCompYn === 'N') {
      //지급(수금) 요청 체크

      step = 4;
    } else if (taxbill.rsltCompYn === 'Y') {
      step = 5;
    }
    return step;
  };

  const initialValues = {
    procTyp: taxbill && taxbill.procTyp !== null ? taxbill.procTyp : '',
    procPlanDt:
      taxbill && taxbill.procPlanDt !== null ? taxbill.procPlanDt : '',
    procPlanYn:
      taxbill && taxbill.procPlanYn !== null && taxbill.procPlanYn === 'Y'
        ? true
        : false,

    procCompDt:
      taxbill && taxbill.procCompDt !== null ? taxbill.procCompDt : '',
    procCompYn:
      taxbill && taxbill.procCompYn !== null && taxbill.procCompYn === 'Y'
        ? true
        : false,
  };

  const validationSchema = yup.object({
    procTyp: yup.string().required('지급유형을 선택하세요'),
    procPlanDt: yup.date().nullable(),
    procCompDt: yup.date().nullable(),
  });

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      onSave(values);
      formik.resetForm();
      setSubmitting(false);
    },
  });

  const onSave = async (values) => {

    let dataToSubmit = {
      invUuid: taxbill.invUuid,
      procTyp: values.procTyp,
      procPlanDt: values.procPlanDt
        ? dayjs(values.procPlanDt).format('YYYY-MM-DD')
        : '',
      procPlanYn: values.procPlanYn ? 'Y' : 'N',
      procCompDt: values.procCompDt
        ? dayjs(values.procCompDt).format('YYYY-MM-DD')
        : '',
      procCompYn: values.procCompYn ? 'Y' : 'N',
    };

    //차후 보충 필요
    if (activeStep === 2) {
      dataToSubmit.procPlanUsr = user.usrUuid;
    } else if (activeStep === 3) {
      dataToSubmit.procCompUsr = user.usrUuid;
    }

    try {
      const res = await taxbillApi.editTaxbill(dataToSubmit);
      //console.log(res.data.info);
      if (res.data) {
        dispatch(setTaxbill(res.data.info));
        enqueueSnackbar('수금&지급 정보 저장 성공', {
          variant: 'success',
        });
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  // 서버에서 이 계산서 회차를 다시 읽어와(procCompYn/procCompDt가 BillCollectService에서
  // 파생됐으므로) redux의 taxbill을 최신화한다. 주의: GET /info/{invUuid}(InvoiceController.
  // getInvInf)는 editTaxbill(PUT)과 달리 BillDetailDto({invInfo, roInfo})로 한 겹 더 감싸서
  // 돌려준다 - res.data.info를 그대로 넣으면 reqCompYn 등 평평한 필드가 전부 undefined가 되어
  // activeStep이 0으로 튕긴다(실사용 중 발견한 버그, 2026-10-21). invInfo를 꺼내 써야 한다.
  const refreshTaxbill = async () => {
    const res = await taxbillApi.getTaxbill(taxbill.invUuid);
    if (res.data?.info?.invInfo) {
      dispatch(setTaxbill(res.data.info.invInfo));
    }
  };

  const reportError = (err, fallback) => {
    const message =
      typeof err.response !== 'undefined' ? err.response.data.message : err.message || fallback;
    enqueueSnackbar(message, { variant: 'error' });
  };

  // 수금(지급) 등록 다이얼로그를 열기 전에 계획(선금/중도금/잔금) 배분 정보까지 포함된
  // 현황(BillCollectSummaryDto)을 받아온다 - BillCollectAddDialog가 row.plans/invTot/
  // collectedAmt를 그대로 쓴다(OrderBillReviewContainer와 같은 모양).
  const handleOpenCollect = async () => {
    try {
      const res = await billCollectApi.getSummary(taxbill.invUuid);
      setCollectSummary(res.data?.data || null);
      setCollectDialogOpen(true);
    } catch (err) {
      reportError(err, '수금(지급) 현황을 불러오지 못했습니다.');
    }
  };

  const handleSubmitCollect = async (payload) => {
    setCollectSubmitting(true);
    try {
      await billCollectApi.addCollect(taxbill.invUuid, payload);
      enqueueSnackbar('수금(지급)을 등록했습니다.', { variant: 'success' });
      setCollectDialogOpen(false);
      await refreshTaxbill();
    } catch (err) {
      reportError(err, '수금(지급) 등록에 실패했습니다.');
    } finally {
      setCollectSubmitting(false);
    }
  };

  // 이 회차에 걸린 유효한 수금 전부 취소(OrderBillCancelService.cancelCollection) - 사유 필수.
  const handleConfirmCancelCollection = async (reason) => {
    setCancelDialogOpen(false);
    try {
      await billCancelApi.cancelCollection(taxbill.invUuid, reason);
      enqueueSnackbar('수금(지급)을 취소했습니다.', { variant: 'success' });
      await refreshTaxbill();
    } catch (err) {
      reportError(err, '수금(지급) 취소에 실패했습니다.');
    }
  };

  return (
    <>
      <TaxBillColAndPayForm
        formik={formik}
        activeStep={activeStep}
        onOpenCollect={handleOpenCollect}
        onOpenCancelCollection={() => setCancelDialogOpen(true)}
      />
      <BillCollectAddDialog
        open={collectDialogOpen}
        onClose={() => setCollectDialogOpen(false)}
        row={collectSummary}
        submitting={collectSubmitting}
        onSubmit={handleSubmitCollect}
      />
      <ReasonDialog
        open={cancelDialogOpen}
        title={`수금(지급) 취소 - ${taxbill?.invNo || ''}`}
        required
        onCancel={() => setCancelDialogOpen(false)}
        onConfirm={handleConfirmCancelCollection}
      />
    </>
  );
};

export default TaxBillColAndPayFormContainer;
